import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, LifeBuoy, Search, Trash2 } from "lucide-react";
import client from "@/api/client";
import { toast } from "sonner";
import { TicketDialog } from "@/components/dashboard/DashboardTicketsTab";

const STATUS_TONES = {
  open: "bg-amber-500/10 text-amber-700 border-amber-500/40",
  in_progress: "bg-primary/10 text-primary border-primary/40",
  resolved: "bg-emerald-500/10 text-emerald-700 border-emerald-500/40",
  closed: "bg-muted text-muted-foreground border-border",
};

const PRIORITY_TONES = {
  low: "bg-muted text-muted-foreground",
  medium: "bg-primary/10 text-primary",
  high: "bg-amber-500/10 text-amber-700",
  urgent: "bg-rose-500/10 text-rose-600",
};

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function AdminTicketsManager() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, open_total: 0, unread_total: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== "all") params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const res = await client.get(`/admin/tickets${params.toString() ? `?${params}` : ""}`);
      setItems(res.data?.items || []);
      setSummary({
        total: res.data?.total || 0,
        open_total: res.data?.open_total || 0,
        unread_total: res.data?.unread_total || 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [status, q]);
  useEffect(() => { load(); }, [load]);

  const openTicket = async (ticket_id) => {
    try {
      const res = await client.get(`/tickets/${ticket_id}`);
      setActive(res.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const remove = async (ticket_id) => {
    if (!window.confirm(t("tickets.confirmDelete"))) return;
    try {
      await client.delete(`/admin/tickets/${ticket_id}`);
      toast.success(t("tickets.deleted"));
      setActive(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-tickets-page">
      <div className="flex items-start gap-3 flex-wrap">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <LifeBuoy className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-xl font-semibold">{t("tickets.adminTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("tickets.adminSubtitle")}</p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="rounded-2xl border border-border p-4" data-testid="admin-tickets-stat-total">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("tickets.statTotal")}</p>
          <p className="font-display text-2xl font-semibold mt-1">{summary.total}</p>
        </Card>
        <Card className="rounded-2xl border border-border p-4" data-testid="admin-tickets-stat-open">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("tickets.statOpen")}</p>
          <p className="font-display text-2xl font-semibold mt-1 text-amber-600">{summary.open_total}</p>
        </Card>
        <Card className="rounded-2xl border border-border p-4" data-testid="admin-tickets-stat-unread">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("tickets.statUnread")}</p>
          <p className="font-display text-2xl font-semibold mt-1 text-rose-600">{summary.unread_total}</p>
        </Card>
      </div>

      <Card className="rounded-2xl border border-border p-4 flex items-center gap-3 flex-wrap">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-xl w-[180px]" data-testid="admin-tickets-filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("tickets.filterAll")}</SelectItem>
            <SelectItem value="open">{t("tickets.status.open")}</SelectItem>
            <SelectItem value="in_progress">{t("tickets.status.in_progress")}</SelectItem>
            <SelectItem value="resolved">{t("tickets.status.resolved")}</SelectItem>
            <SelectItem value="closed">{t("tickets.status.closed")}</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("tickets.searchPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="rounded-xl pl-9"
            data-testid="admin-tickets-search"
          />
        </div>
      </Card>

      <Card className="rounded-2xl border border-border p-4" data-testid="admin-tickets-list">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center" data-testid="admin-tickets-empty">
            {t("tickets.adminEmpty")}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((tk) => (
              <li
                key={tk.ticket_id}
                className={`grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-3 py-3 px-2 rounded-lg hover:bg-muted/30 transition-colors ${tk.unread_by_admin ? "bg-primary/5" : ""}`}
                data-testid={`admin-tickets-item-${tk.ticket_id}`}
              >
                <span className={`h-2 w-2 rounded-full ${tk.unread_by_admin ? "bg-primary" : "bg-transparent"}`} />
                <button
                  type="button"
                  className="text-left min-w-0"
                  onClick={() => openTicket(tk.ticket_id)}
                  data-testid={`admin-tickets-open-${tk.ticket_id}`}
                >
                  <p className="font-medium text-sm truncate">{tk.subject}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {tk.user_email} · {fmtDateTime(tk.updated_at)} · {tk.message_count} {t("tickets.replies")}
                  </p>
                </button>
                <Badge variant="outline" className={`rounded-full text-[10px] capitalize ${STATUS_TONES[tk.status]}`}>
                  {t(`tickets.status.${tk.status}`)}
                </Badge>
                <Badge className={`rounded-full text-[10px] capitalize ${PRIORITY_TONES[tk.priority]}`}>
                  {t(`tickets.priority${tk.priority.charAt(0).toUpperCase() + tk.priority.slice(1)}`)}
                </Badge>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive p-1"
                  onClick={() => remove(tk.ticket_id)}
                  aria-label="Delete ticket"
                  data-testid={`admin-tickets-delete-${tk.ticket_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <TicketDialog
        ticket={active}
        onClose={() => { setActive(null); load(); }}
        canManage={true}
        onSent={() => openTicket(active.ticket_id)}
        onManaged={() => openTicket(active.ticket_id)}
      />
    </div>
  );
}

export default AdminTicketsManager;
